// Das grosse, inline editierbare Titelfeld der Detailansichten.
//
// WOFUER: Aufgaben- und Schritttitel duerfen bis zu 200 bzw. 500 Zeichen lang
// sein, die Detailansichten zeigten sie aber in einem einzeiligen <input>. Lange
// Titel wurden mitten im Wort abgeschnitten, auf 390px blieb neben den Knoepfen
// der Kopfzeile nur "#1 Be" sichtbar. Ein mitwachsendes <textarea> bricht um
// statt abzuschneiden — der Titel ist das, woran man die Aufgabe erkennt, er
// muss vollstaendig lesbar sein.
//
// Verhalten wie vorher beim <input>: Enter legt keinen Zeilenumbruch an,
// sondern bestaetigt (onEnter, sonst Fokus verlassen → onBlur speichert).
// Eingefuegte Zeilenumbrueche werden zu Leerzeichen.

import { useLayoutEffect, useRef } from "preact/hooks";
import type { JSX } from "preact";

export interface TitleFieldProps {
  value: string;
  onValue?: (next: string) => void;
  onBlur?: (value: string) => void;
  /** Enter gedrueckt. Ohne Handler verlaesst Enter das Feld (→ onBlur). */
  onEnter?: () => void;
  readOnly?: boolean;
  placeholder?: string;
  maxLength?: number;
  /** Zugaenglicher Name — Pflicht, ein Platzhalter ist kein Label. */
  label: string;
  class?: string;
}

export function TitleField(props: TitleFieldProps): JSX.Element {
  const { value, onValue, onBlur, onEnter, readOnly, placeholder, maxLength, label } = props;
  const ref = useRef<HTMLTextAreaElement>(null);

  // Hoehe an den Inhalt anpassen (auch bei Breitenwechsel des Dialogs).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = (): void => {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    };
    fit();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [value]);

  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      readOnly={readOnly}
      placeholder={placeholder}
      maxLength={maxLength}
      aria-label={label}
      onInput={(e) => {
        const el = e.target as HTMLTextAreaElement;
        const clean = el.value.replace(/\r?\n/g, " ");
        if (clean !== el.value) el.value = clean;
        onValue?.(clean);
      }}
      onBlur={(e) => onBlur?.((e.target as HTMLTextAreaElement).value)}
      onKeyDown={(e) => {
        if (e.key !== "Enter" || e.shiftKey || e.isComposing) return;
        e.preventDefault();
        if (onEnter) onEnter();
        else (e.target as HTMLTextAreaElement).blur();
      }}
      class={`block w-full resize-none overflow-hidden text-pretty break-words [overflow-wrap:anywhere] ${props.class ?? ""}`}
    />
  );
}
